import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useFamilyPortalApiClient } from '../../auth/hooks/useFamilyPortalApiClient'
import { buildConsentConfigResponse, type ConsentConfigPayload } from '../../consent/consentConfig'
import type { ConsentContext, SecondaryConsentRow } from '../../consent/consentOptions/types'

type SecondaryConsentStateResponse = {
  itemStates?: Record<string, boolean>
}

export type OutstandingSecondaryConsentItem = {
  id: string
  title: string
}

type OutstandingSecondaryConsentsState = {
  loading: boolean
  items: OutstandingSecondaryConsentItem[]
}

function isOutstanding(itemId: string, itemStates: Record<string, boolean>, dualSecondary: boolean): boolean {
  if (dualSecondary) {
    return itemStates[`${itemId}:SELF`] === undefined || itemStates[`${itemId}:DEPENDENT`] === undefined
  }
  return itemStates[itemId] === undefined
}

export function getOutstandingSecondaryConsentItems(
  rows: SecondaryConsentRow[],
  itemStates: Record<string, boolean>,
  dualSecondary: boolean
): OutstandingSecondaryConsentItem[] {
  const seen = new Set<string>()
  const outstanding: OutstandingSecondaryConsentItem[] = []

  rows.forEach((row) => {
    if (seen.has(row.item.id)) return
    seen.add(row.item.id)

    if (isOutstanding(row.item.id, itemStates, dualSecondary)) {
      outstanding.push({ id: row.item.id, title: row.item.title })
    }
  })

  return outstanding
}

export function useOutstandingSecondaryConsents(enabled: boolean): OutstandingSecondaryConsentsState {
  const intl = useIntl()
  const apiClient = useFamilyPortalApiClient()
  const [state, setState] = useState<OutstandingSecondaryConsentsState>({ loading: false, items: [] })

  useEffect(() => {
    if (!enabled) {
      setState({ loading: false, items: [] })
      return
    }

    let cancelled = false
    setState({ loading: true, items: [] })

    async function load() {
      try {
        const [contextRes, configRes] = await Promise.all([
          apiClient.get('/consents/context', undefined, true),
          apiClient.get('/config/consents/secondary'),
        ])

        if (cancelled) return
        if (!configRes.ok) {
          setState({ loading: false, items: [] })
          return
        }

        const context = contextRes.ok ? ((await contextRes.json().catch(() => null)) as ConsentContext | null) : null
        // Avoid guessing single vs dual-subject consent state without context.
        if (!context) {
          setState({ loading: false, items: [] })
          return
        }

        const configPayload = (await configRes.json()) as ConsentConfigPayload
        const config = buildConsentConfigResponse(configPayload, intl.locale)

        if (config.secondaryItems.length === 0) {
          setState({ loading: false, items: [] })
          return
        }

        const params = new URLSearchParams({
          configId: config.configId,
          configVersionId: config.configVersionId,
          consentType: 'secondary',
        })
        const stateRes = await apiClient.get(`/consents/secondary?${params.toString()}`)

        if (cancelled) return
        if (!stateRes.ok) {
          setState({ loading: false, items: [] })
          return
        }

        const stateData = (await stateRes.json().catch(() => ({}))) as SecondaryConsentStateResponse
        if (cancelled) return

        const outstanding = getOutstandingSecondaryConsentItems(
          config.secondaryItems,
          stateData.itemStates ?? {},
          context?.secondaryConsentDualSubjects === true
        )
        setState({ loading: false, items: outstanding })
      } catch {
        if (!cancelled) setState({ loading: false, items: [] })
      }
    }

    load().finally(() => {
      if (!cancelled) {
        setState((current) => (current.loading ? { loading: false, items: current.items } : current))
      }
    })

    return () => {
      cancelled = true
    }
  }, [apiClient, enabled, intl.locale])

  return state
}
